"use client";

import { useState, FormEvent, ChangeEvent, useEffect, useRef } from "react";
import { FaCheck, FaTimesCircle, FaTimes } from "react-icons/fa";
import TextInput from "./formComponents/TextInput";
import ListInput from "./formComponents/ListInput";
import NumberInput from "./formComponents/NumberInput";
import AttachmentElement from "./formComponents/AttachmentElement";
import FormButton from "./formComponents/FormButton";

interface SubmitFormProps {
  cloudflareSiteKey: string
}

interface FormData {
  anrede: string
  vorname: string
  nachname: string
  strasse: string
  plz: string
  ort: string
  telefon: string
  email: string
  marke: string
  modell: string
  fahrzeugstyp: string
  baujahr: string
  getriebe: string
  unfallschaden: string
  kraftstoffart: string
  klimaanlage: string
  kilometerstand: string
  preisvorstellung: string
}

export default function SubmitForm({ cloudflareSiteKey }: SubmitFormProps) {
  const [formData, setFormData] = useState<FormData>({
    anrede: 'bitte auswählen',
    vorname: '',
    nachname: '',
    strasse: '',
    plz: '',
    ort: '',
    telefon: '',
    email: '',
    marke: '',
    modell: '',
    fahrzeugstyp: '',
    baujahr: '',
    getriebe: 'bitte auswählen',
    unfallschaden: 'bitte auswählen',
    kraftstoffart: 'bitte auswählen',
    klimaanlage: 'bitte auswählen',
    kilometerstand: '',
    preisvorstellung: '',
  })

  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showDonePopup, setShowDonePopup] = useState(false)
  const [donePopupVisible, setDonePopupVisible] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileError, setTurnstileError] = useState<string | null>(null)
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<number | null>(null)
  const [isMounted, setIsMounted] = useState(false)
  const turnstileRef = useRef<HTMLDivElement | null>(null)
  const turnstileRenderedRef = useRef(false)

  const generateBotQuestion = () => {
    const x = Math.floor(Math.random() * 11)
    const y = Math.floor(Math.random() * 11)
    return {
      question: `Was ist ${x} + ${y}?`,
      answer: `${x + y}`,
    }
  }

  const [currentBotQuestion, setCurrentBotQuestion] = useState({ question: 'Bitte warten...', answer: '0' })
  const [botAnswer, setBotAnswer] = useState('')

  // Set random question on client side only to avoid hydration mismatch
  useEffect(() => {
    setCurrentBotQuestion(generateBotQuestion())
    setIsMounted(true)

    if (!cloudflareSiteKey) {
      setTurnstileError('Cloudflare Site Key fehlt.')
      return
    }

    const renderTurnstile = () => {
      const turnstile = (window as any).turnstile
      if (!turnstile || turnstileRenderedRef.current || !turnstileRef.current) return

      turnstileRef.current.innerHTML = ''
      const widgetId = turnstile.render(turnstileRef.current, {
        sitekey: cloudflareSiteKey,
        callback: (token: string) => {
          setTurnstileToken(token)
          setTurnstileError(null)
        },
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileError('Bitte bestätigen Sie, dass Sie kein Roboter sind.'),
      })

      setTurnstileWidgetId(widgetId)
      turnstileRenderedRef.current = true
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[src*="challenges.cloudflare.com/turnstile/v0/api.js"]')
    if (existingScript) {
      if ((window as any).turnstile) {
        renderTurnstile()
      } else {
        existingScript.addEventListener('load', renderTurnstile, { once: true })
      }
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    script.onload = renderTurnstile
    document.body.appendChild(script)
  }, [cloudflareSiteKey])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0]
    if (file) {
      const newFiles = [...files]
      newFiles[index] = file
      setFiles(newFiles)
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setMessage(null)

    // Bot validation
    if (botAnswer.toLowerCase().trim() !== currentBotQuestion.answer.toLowerCase()) {
      setMessage({ type: 'error', text: 'Die Sicherheitsfrage wurde falsch beantwortet. Bitte versuchen Sie es erneut.' })
      setLoading(false)
      return
    }

    if (!turnstileToken) {
      setMessage({ type: 'error', text: 'Bitte bestätigen Sie das Captcha.' })
      setLoading(false)
      return
    }

    const formDataToSend = new FormData()

    // Add form fields
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value)
    })

    // Add files
    files.forEach((file, index) => {
      if (file) {
        formDataToSend.append(`file_${index}`, file)
      }
    })

    formDataToSend.append('cf-turnstile-response', turnstileToken)

    try {
      const response = await fetch('/api/submit-form', {
        method: 'POST',
        body: formDataToSend,
      })

      const result = await response.json()

      if (response.ok) {
        setMessage({ type: 'success', text: 'Anfrage erfolgreich versendet! Wir werden uns in Kürze bei Ihnen melden.' })
        setFormData({
          anrede: 'bitte auswählen',
          vorname: '',
          nachname: '',
          strasse: '',
          plz: '',
          ort: '',
          telefon: '',
          email: '',
          marke: '',
          modell: '',
          fahrzeugstyp: '',
          baujahr: '',
          getriebe: 'bitte auswählen',
          unfallschaden: 'bitte auswählen',
          kraftstoffart: 'bitte auswählen',
          klimaanlage: 'bitte auswählen',
          kilometerstand: '',
          preisvorstellung: '',
        })
        setFiles([])
        setTurnstileToken('')
        if ((window as any).turnstile && turnstileWidgetId !== null) {
          ;(window as any).turnstile.reset(turnstileWidgetId)
        }
        setShowDonePopup(true)
        setTimeout(() => setDonePopupVisible(true), 100)
      } else {
        setMessage({ type: 'error', text: result.error || 'Es gab einen Fehler beim Senden der Anfrage.' })
      }
    } catch {
      setMessage({ type: 'error', text: 'Verbindungsfehler. Bitte versuchen Sie es später erneut.' })
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      anrede: 'bitte auswählen',
      vorname: '',
      nachname: '',
      strasse: '',
      plz: '',
      ort: '',
      telefon: '',
      email: '',
      marke: '',
      modell: '',
      fahrzeugstyp: '',
      baujahr: '',
      getriebe: 'bitte auswählen',
      unfallschaden: 'bitte auswählen',
      kraftstoffart: 'bitte auswählen',
      klimaanlage: 'bitte auswählen',
      kilometerstand: '',
      preisvorstellung: '',
    })
    setFiles([])
    setMessage(null)
    setBotAnswer('')
    setTurnstileToken('')
    if ((window as any).turnstile && turnstileWidgetId !== null) {
      ;(window as any).turnstile.reset(turnstileWidgetId)
    }
    setCurrentBotQuestion(generateBotQuestion())
  }

  // Done Popup close handler
  const closeDonePopup = () => {
    setDonePopupVisible(false)
    setTimeout(() => setShowDonePopup(false), 300)
  }

  return (
    <>
      {/* Done Popup */}
      {showDonePopup && (
        <div className={`fixed inset-0 z-50 flex items-end justify-center p-4 ${donePopupVisible ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}>
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeDonePopup}></div>
          <div className={`relative bg-white rounded-lg shadow-xl max-w-md w-full transform transition-transform duration-300 ${donePopupVisible ? "translate-y-0" : "translate-y-full"}`}>
            <div className="flex items-center justify-between p-4 border-b border-primary-light">
              <div className="flex items-center gap-3">
                <FaCheck className="text-primary-light text-xl" />
                <h3 className="text-lg font-semibold text-primary-dark">Erfolgreich gesendet</h3>
              </div>
              <button
                onClick={closeDonePopup}
                className="text-primary-dark hover:text-primary-light transition-colors"
                aria-label="Schließen"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-6 text-center">
              <p className="text-primary-dark text-base mb-4">Ihre Anfrage wurde erfolgreich versendet!<br/>Wir werden uns in Kürze bei Ihnen melden.</p>
              <FormButton label="Schließen" onClick={closeDonePopup} variant="primary" />
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} id="contact-form" className="space-y-8 w-full rounded-xl  p-8 md:p-12 mt-[-40px]">
        {message && (
          <div
            className={`p-4 md:p-6 rounded-lg flex items-start gap-3 ${
              message.type === "success"
                ? "bg-green-50 border border-green-200"
                : "bg-red-50 border border-red-200"
            }`}
          >
            {message.type === "success" ? (
              <FaCheck className="text-green-600 flex-shrink-0 mt-1" />
            ) : (
              <FaTimesCircle className="text-red-600 flex-shrink-0 mt-1" />
            )}
            <p className={message.type === "success" ? "text-green-800" : "text-red-800"}>{message.text}</p>
          </div>
        )}

        {/* Kontaktdaten Section */}
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <ListInput
              label="Anrede"
              name="anrede"
              value={formData.anrede}
              onChange={handleInputChange}
              options={["bitte auswählen", "Divers", "Frau", "Herr"]}
            />
            <TextInput
              label="Vorname"
              name="vorname"
              value={formData.vorname}
              onChange={handleInputChange}
              placeholder="Vorname"
            />
            <TextInput
              label="Nachname"
              name="nachname"
              value={formData.nachname}
              onChange={handleInputChange}
              placeholder="Nachname"
            />
            <TextInput
              label="Straße"
              name="strasse"
              value={formData.strasse}
              onChange={handleInputChange}
              placeholder="Straße"
            />
            <TextInput
              label="PLZ"
              name="plz"
              value={formData.plz}
              onChange={handleInputChange}
              placeholder="PLZ"
            />
            <TextInput
              label="Ort"
              name="ort"
              value={formData.ort}
              onChange={handleInputChange}
              placeholder="Ort"
            />
            <TextInput
              label="Telefon"
              name="telefon"
              value={formData.telefon}
              onChange={handleInputChange}
              placeholder="Telefon"
              required
              type="tel"
            />
            <TextInput
              label="E-Mail Adresse"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="E-Mail Adresse"
              type="email"
            />
          </div>
        </div>

        {/* Technische Details Section */}
        <div>
          <h3 className="text-xl md:text-2xl font-bold mb-6 text-primary-dark">Technische Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <TextInput
              label="Marke"
              name="marke"
              value={formData.marke}
              onChange={handleInputChange}
              placeholder="z.B. VW, BMW, Mercedes"
              required
            />
            <TextInput
              label="Modell"
              name="modell"
              value={formData.modell}
              onChange={handleInputChange}
              placeholder="z.B. Golf, A3, C-Klasse"
              required
            />
            <TextInput
              label="Fahrzeugstyp"
              name="fahrzeugstyp"
              value={formData.fahrzeugstyp}
              onChange={handleInputChange}
              placeholder="z.B. Limousine, SUV, Kombi"
            />
            <NumberInput
              label="Baujahr"
              name="baujahr"
              value={formData.baujahr}
              onChange={handleInputChange}
              placeholder="z.B. 2015"
              required
            />
            <ListInput
              label="Getriebe"
              name="getriebe"
              value={formData.getriebe}
              forceType={"select"}
              onChange={handleInputChange}
              options={["bitte auswählen", "Schaltgetriebe", "Automatik", "Tiptronic"]}
            />
            <ListInput
              label="Unfallschaden"
              name="unfallschaden"
              value={formData.unfallschaden}
              onChange={handleInputChange}
              options={["bitte auswählen", "Ja", "Nein", "Schaden behoben"]}
            />
            <ListInput
              label="Kraftstoffart"
              name="kraftstoffart"
              value={formData.kraftstoffart}
              onChange={handleInputChange}
              options={["bitte auswählen", "Benzin", "Diesel", "Gas", "Elektro", "Sonstiges"]}
              required
            />
            <ListInput
              label="Klimaanlage"
              name="klimaanlage"
              value={formData.klimaanlage}
              onChange={handleInputChange}
              options={["bitte auswählen", "vorhanden", "nicht vorhanden"]}
            />
            <TextInput
              label="Kilometerstand"
              name="kilometerstand"
              value={formData.kilometerstand}
              onChange={handleInputChange}
              placeholder="z.B. 150000 km"
            />
            <TextInput
              label="Preisvorstellung"
              name="preisvorstellung"
              value={formData.preisvorstellung}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        {/* File Upload Section */}
        <div>
          <h3 className="text-xl md:text-2xl font-bold mb-4 text-primary-dark">Bilder hochladen</h3>
          <p className="text-sm text-primary-dark mb-6 bg-primary-light/10 p-4 rounded">
            Bitte laden Sie vorzugsweise Bilder Ihres Fahrzeugs hoch. Aus technischen Gründen können nur Bilder mit den Endungen *.jpg, *.gif oder *.png hochgeladen werden. (Max. 3 Bilder)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((index) => (
              <AttachmentElement
                key={index}
                label="Datei hochladen"
                name="file"
                onChange={handleFileChange}
                index={index}
              />
            ))}
          </div>
        </div>

        {/* Bot Validation */}
        <div>
          <h3 className="text-xl md:text-2xl font-bold mb-6 text-primary-dark">Sicherheitsfrage</h3>
          <div className="bg-primary-light/10 p-4 md:p-6 rounded-lg">
            <label htmlFor="botAnswer" className="block text-sm md:text-base font-medium text-primary-dark mb-2">
              {currentBotQuestion?.question}
            </label>
            <TextInput
              label="Ihre Antwort"
              name="botAnswer"
              value={botAnswer}
              onChange={(e) => setBotAnswer(e.target.value)}
              placeholder="Ihre Antwort"
              required
            />
            <p className="text-sm text-primary-dark mt-2">Diese Frage hilft uns, Spam zu vermeiden.</p>
          </div>
        </div>

        {/* Cloudflare Turnstile */}
        <div>
          <div ref={turnstileRef} className="turnstile-widget" />
          {turnstileError && <p className="text-red-600 text-sm mt-2">{turnstileError}</p>}
          {isMounted && !cloudflareSiteKey && (
            <p className="text-red-600 text-sm mt-2">Cloudflare Turnstile ist nicht konfiguriert.</p>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-4 flex-col sm:flex-row pt-6">
          <FormButton
            label={loading ? "Wird versendet..." : "ANFRAGE SENDEN"}
            type="submit"
            disabled={loading}
            variant="primary"
          />
          <FormButton
            label="Zurücksetzen"
            type="button"
            onClick={handleReset}
            disabled={loading}
            variant="secondary"
          />
        </div>
      </form>
    </>
  );
}
